/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Table, AutoSizer, Column } from 'react-virtualized';
import Draggable from 'react-draggable';
import { useTheme } from '@material-ui/core';
import Divider from '@mui/material/Divider';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { Check } from '@material-ui/icons';
import { green } from '@material-ui/core/colors';

const styles = makeStyles(theme => ({
  '@global': {
    '.ReactVirtualized__Table__headerRow': {
      ...(theme.palette.type === 'light' && {
        borderBottom: '1px solid #d9dcde',
      }),
      ...(theme.palette.type === 'dark' && {
        borderBottom: '1px solid #5c5c5c',
      }),
    },
    '.ReactVirtualized__Grid': {
      ...(theme.palette.type === 'dark' && {
        '&::-webkit-scrollbar': {
          width: '1.3em',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#212121',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#5e5e5e',
          borderRadius: '7px',
          border: '3px solid #212121',
        },
      }),
    },
  },
  headerColumn: {
    backgroundColor: '#F5F7F7',
  },
  headerColumnDark: {
    backgroundColor: '#1C1C1C',
  },
  tableHeader: {
    color: '#7A7C7C',
    textTransform: 'none !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 0,
  },
  tableHeaderDark: {
    color: '#AEAFAE',
    textTransform: 'none !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 0,
  },
  tableHeaderTruncatedText: {
    flex: 'auto',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  tableHeaderTruncatedTextDark: {
    flex: 'auto',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  tableCellDark: {
    color: '#FFFFFF',
  },
  tableCell: {
    color: '#000',
  },
  tableCellCentered: {
    textAlign: 'center',
  },
  tableCellCenteredDark: {
    color: '#FFFFFF',
    textAlign: 'center',
  },
  tableAlternateRow: {
    backgroundColor: '#fcfdfe',
    borderBottom: '1px solid #d9dcde',
    '&:hover': {
      backgroundColor: '#ECF0F1',
    },
  },
  tableAlternateRowDark: {
    backgroundColor: '#262C2F',
    borderBottom: '1px solid #5c5c5c',
    '&:hover': {
      backgroundColor: '#3D4749',
    },
  },
  tableRow: {
    borderBottom: '1px solid #d9dcde',
    '&:hover': {
      backgroundColor: '#ECF0F1',
    },
  },
  tableRowDark: {
    borderBottom: '1px solid #5c5c5c',
    backgroundColor: '#2D3536',
    '&:hover': {
      backgroundColor: '#3D4749',
    },
  },
  virtualizedTable: {
    fontSize: '12px',
    fontFamily:
      '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif',
    width: '100%',
    height: '100%',
    borderLeft: '1px solid #bdc3c7',
    borderTop: '1px solid #bdc3c7',
  },
  virtualizedTableDark: {
    fontSize: '12px',
    fontFamily:
      '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif',
    width: '100%',
    height: '100%',
    backgroundColor: '#1C1C1C',
  },
  dragHandle: {
    height: '15px !important',
    backgroundColor: 'transparent',
    flex: '0 0 16px',
    zIndex: 2,
    cursor: 'col-resize',
    color: theme.palette.type === 'dark' ? '#AEAFAE' : '#0000008a',
    borderColor: theme.palette.type === 'dark' ? '#AEAFAE !important' : '#0000008a',
    opacity: theme.palette.type === 'dark' ? '0.5' : '1',
    '&:hover': {
      cursor: 'col-resize',
    },
  },
  dragHandleActive: {
    color: '#0000008a',
    zIndex: 3,
  },
}));

const initialWidths = {
  quantity: 0.5,
  is_ours: 0.3,
  zone: 0.6,
  section: 0.7,
  row: 0.6,
  original_price: 0.8,
  total_price: 0.9,
  all_in_price: 1,
  deal_score: 1,
  splits: 0.6,
};

const ListingsTable = props => {
  const classes = styles();
  const [widths, setWidths] = useState(initialWidths);
  const theme = useTheme();
  const renderBroadcast = ({ rowData }) => {
    const ours = rowData?.is_ours;
    return ours ? <Check style={{ color: green[500] }} /> : null;
  };

  const { listings, marketplace } = props;

  const getNextKey = currentKey => {
    const keys = Object.keys(initialWidths);
    const currentIndex = keys.indexOf(currentKey);
    // Check if currentIndex is valid and not the last key
    if (currentIndex !== -1 && currentIndex < keys.length - 1) {
      return keys[currentIndex + 1];
    }
    return null; // Return null if currentKey is the last key or not found
  };

  const resizeRow = ({ dataKey, deltaX }) => {
    const currentWidths = widths;
    const percentageDelta = deltaX / 100;
    const nextKey = getNextKey(dataKey);
    const newWidths = {
      ...currentWidths,
      [dataKey]: currentWidths[dataKey] + percentageDelta,
      [nextKey]: currentWidths[nextKey] - percentageDelta,
    };
    setWidths(newWidths);
  };

  const headerRenderer = ({ dataKey, label }) => {
    return (
      <React.Fragment key={dataKey}>
        <div
          className={
            theme.palette.type !== 'dark' ? classes.tableHeaderTruncatedText : classes.tableHeaderTruncatedTextDark
          }
        >
          {label}
        </div>
        <Draggable
          axis="x"
          defaultClassName={classes.dragHandle}
          defaultClassNameDragging={classes.dragHandleActive}
          onDrag={(event, { deltaX }) => resizeRow({ dataKey, deltaX })}
          position={{ x: 0 }}
          zIndex={999}
        >
          <Divider flexitem orientation="vertical" className={classes.divider} />
        </Draggable>
      </React.Fragment>
    );
  };

  const tableRowClass = index => {
    if (index === -1) {
      return theme.palette.type !== 'dark' ? classes.headerColumn : classes.headerColumnDark;
    }
    const alternate = index % 2 === 0;
    if (alternate) {
      return theme.palette.type !== 'dark' ? classes.tableAlternateRow : classes.tableAlternateRowDark;
    }
    return theme.palette.type !== 'dark' ? classes.tableRow : classes.tableRowDark;
  };

  return (
    <div
      data-testid="listings-table-container"
      className={theme.palette.type !== 'dark' ? classes.virtualizedTable : classes.virtualizedTableDark}
      style={{ overflowX: marketplace === 'seatgeek' && 'auto' }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <Table
            width={
              marketplace === 'seatgeek'
                ? Math.max(width, Object.values(initialWidths).reduce((acc, num) => acc + num, 0) * 100)
                : width || 1000
            }
            height={height || 1000}
            rowHeight={30}
            headerHeight={35}
            header
            className={theme.palette.type !== 'dark' ? classes.table : classes.tableDark}
            headerClassName={theme.palette.type !== 'dark' ? classes.headerColumn : classes.headerColumnDark}
            rowCount={listings.length}
            rowGetter={({ index }) => listings[index]}
            rowClassName={({ index }) => {
              return tableRowClass(index);
            }}
          >
            <Column
              dataKey="quantity"
              label="Qty"
              width={widths.quantity * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
              headerRenderer={headerRenderer}
            />
            <Column
              dataKey="is_ours"
              label="Ours"
              width={widths.is_ours * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCellCentered : classes.tableCellCenteredDark}
              cellRenderer={renderBroadcast}
              headerRenderer={headerRenderer}
            />
            {marketplace && marketplace === 'ticketnetwork' && (
              <Column
                dataKey="zone"
                label="Zone"
                width={widths.zone * 100}
                headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
                className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
                headerRenderer={headerRenderer}
              />
            )}
            <Column
              dataKey="section"
              label="Section"
              width={widths.section * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
              headerRenderer={headerRenderer}
            />
            <Column
              dataKey="row"
              label="Row"
              width={widths.row * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
              headerRenderer={headerRenderer}
            />
            <Column
              dataKey="original_price"
              label="API Original Price"
              width={widths.original_price * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
              headerRenderer={headerRenderer}
            />
            <Column
              dataKey="total_price"
              label="Price"
              width={widths.total_price * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
              headerRenderer={headerRenderer}
            />
            {marketplace && marketplace === 'seatgeek' && (
              <Column
                dataKey="all_in_price"
                label="All-in Price"
                width={widths.all_in_price * 100}
                headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
                className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
                headerRenderer={headerRenderer}
              />
            )}
            {marketplace && marketplace === 'seatgeek' && (
              <Column
                dataKey="deal_score"
                label="Deal Score"
                width={widths.deal_score * 100}
                headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
                className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
                headerRenderer={headerRenderer}
              />
            )}
            <Column
              dataKey="splits"
              label="Splits"
              width={widths.splits * 100}
              headerClassName={theme.palette.type !== 'dark' ? classes.tableHeader : classes.tableHeaderDark}
              className={theme.palette.type !== 'dark' ? classes.tableCell : classes.tableCellDark}
              headerRenderer={headerRenderer}
            />
          </Table>
        )}
      </AutoSizer>
    </div>
  );
};

ListingsTable.propTypes = {
  listings: PropTypes.array.isRequired,
  marketplace: PropTypes.string.isRequired,
};

export default ListingsTable;
